import React, { FC, useEffect } from 'react';
import TableSimple from 'src/components/TableSimple';
import { CustomRenderType, TableColumnType } from 'src/types/table';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import translate from '@translate';
import { OrderPalletType } from 'src/types/order';
import { useDispatch, useSelector } from 'react-redux';
import { RootStoreType } from 'src/types/store';
import { getOrderPalletsFetch } from '../../resources/orderDetail/reducer';
import { useParams } from 'react-router-dom';
import TextFieldDebounced from 'src/components/TextFieldDebounced';

const getInputName = (rowId: number | string | undefined, columnKey: string): string => {
    return `${rowId}_${columnKey}`;
};

export interface PalletsTablePropsType {
    data?: OrderPalletType[];
    onChange: (value: any, clearErrors?: boolean) => void;
    disableEdit?: boolean;
}

const PalletsTable: FC<PalletsTablePropsType> = (props) => {
    const dispatch = useDispatch();
    const { idOrder } = useParams();
    const orderPallets = useSelector((state: RootStoreType) => state.orderDetail.orderPallets);
    const palletTypes = useSelector((state: RootStoreType) => state.orderDetail.palletTypes);

    useEffect(() => {
        if (idOrder && !orderPallets) {
            dispatch(getOrderPalletsFetch(idOrder));
        }
    }, [idOrder, orderPallets]);

    const handleDeleteRow = (row: OrderPalletType) => {
        if (!props.data) return;
        const updatedData = props.data.filter((item) => item.ID !== row.ID);
        props.onChange(updatedData, true);  // Then notify parent and clear errors
    };

    const handleChangeValue = (key: string, value: any, error: boolean, rowId: number | string | undefined) => {
        // Find the pallet in the data array
        if (!props.data) return;
        const palletIndex = props.data.findIndex((item) => item.ID === rowId);

        // Create a copy of the data array
        const updatedData = [...props.data];

        // Update the specific field in the pallet
        updatedData[palletIndex] = {
            ...updatedData[palletIndex],
            [key]: value
        };

        // Update the parent component with the complete set of pallets
        // Pass clearErrors=true when there's no error to clear the error state in the parent
        props.onChange(updatedData, !error);
    };

    const columns: TableColumnType[] = [
        { key: 'PALLET_TYPE', label: 'palletType', customRender: true },
        { key: 'WEIGHT', label: 'weight', type: 'number-filter', customRender: true },
        { key: 'LENGTH', label: 'length', type: 'number-filter', customRender: true },
        { key: 'WIDTH', label: 'width', type: 'number-filter', customRender: true },
        { key: 'HEIGHT', label: 'height', type: 'number-filter', customRender: true }
    ];

    const customRender: CustomRenderType = (column, row) => {
        if (props.disableEdit) {
            // If editing is disabled, just display the values
            if (column.key === 'PALLET_TYPE_ID' && row.PALLET_TYPE && row.PALLET_TYPE.NAME) {
                return row.PALLET_TYPE.NAME;
            }
            return row[column.key];
        }

        const name = getInputName(row.ID, column.key);
        switch (column.key) {
            case 'PALLET_TYPE_ID':
                return row.PALLET_TYPE && row.PALLET_TYPE.NAME;
            case 'WEIGHT':
                return (
                    <TextFieldDebounced
                        value={row[column.key]}
                        onChange={(value, error) => handleChangeValue(column.key, value, error, row.ID)}
                        name={name}
                        fullWidth
                        type="numberLocale"
                        min={0}
                        step="0.01"
                        required
                        disabled={props.disableEdit}
                    />
                );
            case 'LENGTH':
            case 'WIDTH':
            case 'HEIGHT':
                return (
                    <TextFieldDebounced
                        value={row[column.key]}
                        onChange={(value, error) => handleChangeValue(column.key, value, error, row.ID)}
                        name={name}
                        fullWidth
                        type="numberLocale"
                        min={0}
                        step="1"
                        required
                        disabled={props.disableEdit}
                    />
                );
            default:
                return row[column.key];
        }
    };

    return (
        <div data-testid="order-pallets-tab">
            <TableSimple
                initSort={{ column: 'ID', direction: 'ASC', createIdNumber: true }}
                rowIdKey="ID"
                rows={props.data || []}
                columsLabelPrefix="orderDetail.palletTable"
                allColumns={columns}
                rowActions={[
                    {
                        icon: <HighlightOffIcon />,
                        label: translate('orderDetail.palletTable.action.removePallet'),
                        onClick: handleDeleteRow,
                        hiddenFce: () => !!props.disableEdit
                    }
                ]}
                sumCols={{
                    WEIGHT: true
                }}
                customRender={customRender}
                disableFilter={true}
            />
        </div>
    );
};

export default PalletsTable;

